import { Trans } from '@lingui/react/macro';
import React from 'react';
import { useSelector } from 'react-redux';
import { searchChangeRequests } from '@/services/change-requests';
import {
    getResidentialDashboardData,
    getResidentialUnitExtraData,
    getResidentialUnitPresetSelections,
    getResidentialUnitSpending,
} from '@/services/residential-units';
import { IState } from '@/state/IState';
import { ResidentialUnitSpendingRow } from '@/types';
import { findCategoryName } from '@/utils/categories';
import { useAsyncState } from '@/utils/hooks';
import { ResidentialUnitDeadlineList } from '../../ResidentialUnitDeadlineList';
import theme from './ResidentialUnitDashboard.module.scss';
import ResidentialUnitDashboardChangeRequests from './ResidentialUnitDashboardChangeRequests';
import ResidentialUnitDashboardInfo from './ResidentialUnitDashboardInfo';
import { CurrencySymbol } from '@/components/localization';
import { BygrModule, DashboardCardGrid, DashboardDataCard, NumberDisplay, SimpleBarChart, UnitModulesCard } from '@ygtechnologies/bygr-frontend-component-library';

interface ResidentialUnitDashboardProps {
    residentialUnitId: number;
}

const ResidentialUnitDashboard: React.FC<ResidentialUnitDashboardProps> = ({ residentialUnitId }) => {
    const [residentialUnit] = useAsyncState(() => getResidentialUnitExtraData(residentialUnitId), [residentialUnitId], null);
    const [dashboardData] = useAsyncState(() => getResidentialDashboardData(residentialUnitId), [residentialUnitId], null);
    const [spending] = useAsyncState(() => getResidentialUnitSpending(residentialUnitId), [residentialUnitId], null);
    const [selectedPresets] = useAsyncState(
        () => getResidentialUnitPresetSelections(residentialUnitId),
        [residentialUnit],
        null
    );
    const project = useSelector((state: IState) => state.global.activeProject);

    const itemCategories = useSelector((state: IState) => state.global.itemCategories);

    const [changeRequests] = useAsyncState(
        () => searchChangeRequests({ units: [residentialUnitId] }),
        [residentialUnit],
        null
    );

    const getCategoryName = (id: number) => {
        return findCategoryName(itemCategories, id);
    };

    return (
        <DashboardCardGrid rows={3} columns={4} className={theme.container}>
            <DashboardDataCard
                rowSpan={2}
                title={<Trans>Unit info</Trans>}
                contentAlignment="top"
                loading={!residentialUnit}>
                {() => <ResidentialUnitDashboardInfo residentialUnit={residentialUnit!} />}
            </DashboardDataCard>

            <DashboardDataCard rowSpan={1} title={<Trans>Modules</Trans>} contentAlignment="top" loading={!residentialUnit}>
                {() => (
                    <UnitModulesCard
                        projectId={project?.id || 0}
                        unitId={residentialUnitId}
                        currentModuleId={BygrModule.ChangeManagement}
                    />
                )}
            </DashboardDataCard>

            <DashboardDataCard
                contentAlignment="top"
                linkTo="options"
                loading={!residentialUnit}
                contentClassName={theme.deadlines}
                title={<Trans>Deadlines</Trans>}>
                {() => (
                    <>
                        {residentialUnit!.deadlines.length !== 0 ? (
                            <ResidentialUnitDeadlineList type="dashboard" deadlines={residentialUnit!.deadlines} />
                        ) : (
                            <span className={theme.noDataMessage}>
                                <Trans>No deadlines</Trans>
                            </span>
                        )}
                    </>
                )}
            </DashboardDataCard>

            <DashboardDataCard loading={!selectedPresets} contentAlignment="top" title={<Trans>Styles</Trans>}>
                {() => (
                    <dl>
                        {selectedPresets!.presetSelections.map((item) => (
                            <React.Fragment key={item.presetGroup.id}>
                                <dt>{item.presetGroup.name}</dt>
                                <dd>{item.selectedPreset?.name ?? <Trans>N/A</Trans>}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                )}
            </DashboardDataCard>

            <DashboardDataCard colSpan={2} loading={!changeRequests} linkTo="changes" title={<Trans>Change requests</Trans>}>
                {() => <ResidentialUnitDashboardChangeRequests changeRequests={changeRequests!} />}
            </DashboardDataCard>

            <DashboardDataCard loading={!dashboardData} linkTo="documents" title={<Trans>Number of documents</Trans>}>
                {() => dashboardData!.numberOfDocuments}
            </DashboardDataCard>

            <DashboardDataCard loading={!dashboardData} linkTo="orders" title={<Trans>Number of orders</Trans>}>
                {() => dashboardData!.numberOfOrders}
            </DashboardDataCard>

            <DashboardDataCard
                infoIconContent={<Trans>This card indicates the total spend of buyers per discipline.</Trans>}
                title={<Trans id="Total spend per discipline(as seller)">Total spend per discipline</Trans>}
                rowSpan={2}
                loading={!spending}>
                {() => (
                    <>
                        {spending!.details.length === 0 ? (
                            <span className={theme.noDataMessage}>
                                <Trans>No spending</Trans>
                            </span>
                        ) : (
                            <SimpleBarChart<ResidentialUnitSpendingRow>
                                data={spending!.details.map((d) => ({
                                    ...d,
                                    totalSpending: +d.totalSpending.toFixed(0),
                                }))}
                                nameKey={(row) => getCategoryName(row.rootItemCategoryId) || <Trans>Uncategorized</Trans>}
                                valueKey={'totalSpending'}
                            />
                        )}
                    </>
                )}
            </DashboardDataCard>

            <DashboardDataCard loading={!spending || !project} title={<Trans>Total spending</Trans>}>
                {() => (
                    <div>
                        <NumberDisplay value={spending!.totalSpending!} />
                        <small>
                            <CurrencySymbol currency={project!.currencyCode} />
                        </small>
                    </div>
                )}
            </DashboardDataCard>
        </DashboardCardGrid>
    );
};

export default ResidentialUnitDashboard;
